import React from 'react';
import { formatMaintenanceCountdown, MaintenanceState } from '../../services/maintenanceLogic';
import { useMaintenanceCountdown } from '../../hooks/useMaintenanceCountdown';

/**
 * Leiste oben in <main>, solange eine Bauphase angekuendigt ist.
 *
 *   'announced' — helle Leiste mit Countdown, damit niemand mitten in einem
 *                 Bericht ueberrascht wird.
 *   'warning'   — die letzten zwei Minuten in Orange.
 *
 * Der Sekundentakt kommt aus useMaintenanceCountdown, NICHT aus dem Hook:
 * der rechnet in 'announced' nur alle 30 s nach, und bis 10.09.2026 stand
 * die Zahl hier deshalb 30 s lang still („Timer friert ein, erst ein Reload
 * hilft" — Befund des Betreibers bei der ersten Bauphase auf PROD).
 *   'locked'    — fuer alle „wird gesichert …"; fuer den Admin, der „Umgehen" gedrueckt hat:
 *                 dezente Erinnerung samt Knopf zum Beenden. Alle anderen
 *                 sehen in dieser Phase die Vollbild-Wartungsseite.
 *
 * Die Baustellenkante (gelb-schwarz) ist das Erkennungszeichen — dieselbe
 * Sprache wie die Wartungsseite, damit beides als EIN Vorgang gelesen wird.
 *
 * `sticky top-0` + `shrink-0`: <main> ist eine Flex-Spalte mit
 * overflow-hidden; ohne shrink-0 drueckte ein hoher ProjektHub die Leiste
 * auf null Hoehe (dasselbe Problem wie beim ProjectNoticeBanner).
 */
export interface BauphaseBannerProps {
    state: MaintenanceState;
    /** Admin ist waehrend 'locked' drin (nach „Umgehen"). */
    adminInside?: boolean;
    /** Knopf „Bauphase beenden" (nur adminInside). */
    onEnd?: () => void;
}

const STRIPE: React.CSSProperties = {
    height: 6,
    backgroundImage: 'repeating-linear-gradient(45deg, #111827 0 12px, #FACC15 12px 24px)',
};

export const BauphaseBanner: React.FC<BauphaseBannerProps> = ({ state, adminInside = false, onEnd }) => {
    const { phase } = state;
    const { remainingMs } = useMaintenanceCountdown(state);

    if (phase === 'locked') {
        // Die Sekunden zwischen T−0 und der Wartungsseite: das Gate sichert
        // gerade (Flusher, Warteschlange, Journal) und zeigt die Seite erst
        // danach (showScreen wartet auf flushDone). Ohne diese Leiste stuende
        // der Zaehler bei 0:00 und verschwaende — und wer da noch tippt, weiss
        // nicht, dass sein Stand gerade weggeht.
        if (!adminInside) {
            return (
                <div className="sticky top-0 z-[130] shrink-0 bg-orange-600 text-white" role="status" aria-live="polite">
                    <div style={STRIPE} />
                    <div className="px-4 py-1.5 text-xs font-semibold">
                        Bauphase beginnt – Ihre Eingaben werden gesichert …
                    </div>
                </div>
            );
        }
        return (
            <div className="sticky top-0 z-[130] shrink-0 bg-slate-800 text-slate-100" role="status">
                <div style={STRIPE} />
                <div className="px-4 py-1.5 flex items-center gap-3 text-xs">
                    <span className="flex-1 min-w-0">
                        Bauphase läuft, Sie sind als Admin drin.
                    </span>
                    {onEnd && (
                        <button
                            type="button"
                            onClick={onEnd}
                            className="shrink-0 px-2.5 py-1 rounded border border-slate-500 hover:bg-slate-700 font-semibold"
                        >
                            Bauphase beenden
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (phase !== 'announced' && phase !== 'warning') return null;

    const warning = phase === 'warning';
    const tone = warning
        ? 'bg-orange-500 text-white'
        : 'bg-amber-50 text-slate-900 border-b border-amber-200';
    const message = state.message.trim();

    return (
        <div className={`sticky top-0 z-[130] shrink-0 ${tone}`} role="status" aria-live="polite">
            <div style={STRIPE} />
            <div className="px-4 py-2 text-sm flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-0.5">
                <span className="font-semibold whitespace-nowrap">
                    Bauphase in <span className="tabular-nums">{formatMaintenanceCountdown(remainingMs)}</span>
                    {' – '}Ihre Eingaben werden automatisch gesichert.
                </span>
                {message !== '' && (
                    <span className={`min-w-0 truncate ${warning ? 'text-orange-50' : 'text-slate-700'}`} title={message}>
                        {message}
                    </span>
                )}
            </div>
        </div>
    );
};

export default BauphaseBanner;
